import * as React from "react"
import { ChevronDown, X } from "lucide-react"

import { Input } from "./input"
import { cn } from "../../lib/utils"

type FloatingLabelAutocompleteProps = {
  id?: string
  label: React.ReactNode
  required?: boolean
  invalid?: boolean
  errorText?: React.ReactNode
  disabled?: boolean
  dir?: "rtl" | "ltr"
  value?: string
  options: string[]
  onValueChange?: (value: string) => void
  onSelect?: (value: string) => void
  noOptionsText?: React.ReactNode
  maxOptions?: number
  filterFromStart?: boolean
  dataTestId?: string
}

const FloatingLabelAutocomplete = React.forwardRef<
  HTMLInputElement,
  FloatingLabelAutocompleteProps
>(
  (
    {
      id,
      label,
      required,
      invalid,
      errorText,
      disabled,
      dir = "rtl",
      value = "",
      options,
      onValueChange,
      onSelect,
      noOptionsText,
      maxOptions = 500,
      filterFromStart,
      dataTestId,
    },
    ref
  ) => {
    const generatedId = React.useId()
    const inputId = id ?? `floating-autocomplete-${generatedId}`
    const errorId = errorText ? `${inputId}-error` : undefined
    const [open, setOpen] = React.useState(false)
    const [activeIndex, setActiveIndex] = React.useState<number>(-1)
    const wrapperRef = React.useRef<HTMLDivElement>(null)
    const isRtl = dir === "rtl"

    const query = value ?? ""
    const normalizedQuery = query.toLowerCase()
    const filteredOptions = !Array.isArray(options)
      ? []
      : !query
        ? options.slice(0, maxOptions)
        : options
            .filter((option) => {
              const normalizedOption = option.toLowerCase()
              return filterFromStart
                ? normalizedOption.startsWith(normalizedQuery)
                : normalizedOption.includes(normalizedQuery)
            })
            .slice(0, maxOptions)

    const showMenu = open && !disabled
    const shouldFloat = Boolean(query) || showMenu

    const selectOption = (option: string) => {
      onValueChange?.(option)
      onSelect?.(option)
      setOpen(false)
      setActiveIndex(-1)
    }

    const handleFocus = () => {
      if (disabled) return
      setOpen(true)
    }

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      const nextTarget = event.relatedTarget as Node | null
      if (nextTarget && wrapperRef.current?.contains(nextTarget)) {
        return
      }
      setOpen(false)
      setActiveIndex(-1)
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showMenu) return
      if (event.key === "ArrowDown") {
        event.preventDefault()
        setActiveIndex((prev) =>
          Math.min(prev + 1, Math.max(filteredOptions.length - 1, 0))
        )
        return
      }
      if (event.key === "ArrowUp") {
        event.preventDefault()
        setActiveIndex((prev) => Math.max(prev - 1, 0))
        return
      }
      if (event.key === "Enter" && activeIndex >= 0 && filteredOptions[activeIndex]) {
        event.preventDefault()
        selectOption(filteredOptions[activeIndex])
      }
      if (event.key === "Escape") {
        setOpen(false)
        setActiveIndex(-1)
      }
    }

    return (
      <div className="w-full" dir={dir} data-testid={dataTestId}>
        <div className={cn("relative", showMenu && "z-50")} ref={wrapperRef} data-invalid={invalid ? "true" : "false"}>
          <Input
            ref={ref}
            id={inputId}
            value={query}
            required={required}
            disabled={disabled}
            aria-invalid={invalid ? true : undefined}
            aria-errormessage={invalid && errorId ? errorId : undefined}
            aria-expanded={showMenu}
            aria-autocomplete="list"
            autoComplete="off"
            placeholder=" "
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onChange={(event) => {
              onValueChange?.(event.target.value)
              if (!open) setOpen(true)
            }}
            style={{ textAlign: isRtl ? "right" : "left", direction: dir }}
            className={cn(
              "peer box-border h-10 w-full rounded-none border-0 border-b border-lupa-muted bg-lupa-surface px-3 pb-0.5 pt-3.5 font-ploni text-[18px] leading-none text-lupa-text shadow-none ring-0 placeholder:text-transparent focus-visible:border-b-lupa-brand focus-visible:ring-0 disabled:cursor-not-allowed disabled:text-lupa-muted",
              invalid && "border-b-lupa-error focus-visible:border-b-lupa-error"
            )}
          />

          {Boolean(query) && !disabled && (
            <button
              type="button"
              aria-label="clear"
              tabIndex={-1}
              className={cn(
                "absolute top-1/2 -translate-y-1/2 z-20",
                "inline-flex h-6 w-6 items-center justify-center rounded-sm border-0 bg-transparent p-0",
                "text-lupa-muted hover:text-lupa-text hover:bg-lupa-hover",
                isRtl ? "left-6" : "right-6"
              )}
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onClick={(e) => {
                e.stopPropagation()
                onValueChange?.("")
                setOpen(false)
                setActiveIndex(-1)
              }}
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}

          <ChevronDown
            className={cn(
              "pointer-events-none absolute top-1/2 -translate-y-1/2 text-lupa-muted h-4 w-4",
              isRtl ? "left-2" : "right-2",
              showMenu && "rotate-180"
            )}
          />

          <label
            htmlFor={inputId}
            style={{ insetInlineStart: "0.75rem" }}
            className={cn(
              "absolute z-10 select-none bg-transparent px-0 text-lupa-text text-start font-ploni",
              "transition-all duration-200 ease-out",
              shouldFloat
                ? "top-1.5 translate-y-0 scale-100 text-xs leading-none"
                : "top-1/2 -translate-y-1/2 scale-100 text-[14px] leading-none pointer-events-none",
              invalid && "text-lupa-error",
              disabled && "text-lupa-muted",
              isRtl ? "origin-top-right" : "origin-top-left"
            )}
          >
            {label}
            {required && (
              <span className={cn("ms-0.5", disabled ? "text-lupa-muted" : invalid ? "text-lupa-error" : "text-lupa-text")} aria-hidden="true">
                *
              </span>
            )}
          </label>

          {showMenu && (
            <div
              className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-[#e7eaee] bg-white shadow-[0_8px_24px_rgba(64,64,64,0.08)]"
              role="listbox"
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => (
                  <button
                    key={`${option}-${index}`}
                    type="button"
                    role="option"
                    aria-selected={index === activeIndex}
                    tabIndex={-1}
                    className={cn(
                      "block w-full border-0 bg-transparent px-3 py-2 text-start font-ploni text-[14px] text-lupa-text transition-colors",
                      "hover:bg-[#f6f7f8] focus:bg-[#f6f7f8]",
                      index === activeIndex && "bg-[#f6f7f8]"
                    )}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => {
                      event.preventDefault()
                      selectOption(option)
                    }}
                  >
                    {option}
                  </button>
                ))
              ) : (
                <div className="px-3 py-2 font-ploni text-[14px] text-lupa-muted text-start">
                  {noOptionsText}
                </div>
              )}
            </div>
          )}
        </div>

        <p
          id={errorId}
          className={cn(
            "mt-1 min-h-[18px] text-[12px]",
            isRtl ? "text-right" : "text-left",
            invalid ? "text-lupa-error" : "invisible"
          )}
          role={invalid && errorText ? "alert" : undefined}
        >
          {invalid && errorText ? errorText : "\u00A0"}
        </p>
      </div>
    )
  }
)

FloatingLabelAutocomplete.displayName = "FloatingLabelAutocomplete"

export { FloatingLabelAutocomplete }
